import { useCallback, useEffect, useMemo, useState } from "react";

import CustomerPriceOfferContractApi from "../api/customerPriceOfferContract";
import {
  CUSTOMER_OFFER_STATUS_CODES,
  normalizeCustomerPriceOfferStatus,
} from "../utils/pricingQuoteStatus";

export const PRICE_WORKSPACE_STATE = {
  NOT_APPLICABLE: "NotApplicable",
  NOT_SUBMITTED: "NotSubmitted",
  AWAITING: "AwaitingCustomer",
  ACCEPTED: "Accepted",
  REVIEW: "ReviewRequested",
};

const OFFER_STATUS_NAME = {
  [CUSTOMER_OFFER_STATUS_CODES.AcceptedByCustomer]: "AcceptedByCustomer",
  [CUSTOMER_OFFER_STATUS_CODES.RejectedByCustomer]: "RejectedByCustomer",
  [CUSTOMER_OFFER_STATUS_CODES.ApprovedBySolarent]: "ApprovedBySolarent",
};

export const normalizePriceConfirmationWorkspace = (raw) => {
  if (!raw) return null;

  const offerRaw = raw?.offer ?? raw?.Offer ?? null;
  const offerStatus = offerRaw
    ? normalizeCustomerPriceOfferStatus(offerRaw?.status ?? offerRaw?.Status)
    : null;

  return {
    workspaceState: raw?.workspaceState ?? raw?.WorkspaceState ?? "",
    message: raw?.message ?? raw?.Message ?? "",
    hasOffer: raw?.hasOffer ?? raw?.HasOffer ?? false,
    awaitingConfirmation:
      raw?.awaitingConfirmation ?? raw?.AwaitingConfirmation ?? false,
    status:
      raw?.status ??
      raw?.Status ??
      (offerStatus != null ? OFFER_STATUS_NAME[offerStatus] ?? String(offerStatus) : null),
    offer: offerRaw,
  };
};

export const shouldShowCustomerPricingPanel = (workspace) => {
  if (!workspace) return false;

  if (workspace.workspaceState === PRICE_WORKSPACE_STATE.NOT_APPLICABLE) {
    return false;
  }

  return (
    workspace.hasOffer === true ||
    workspace.awaitingConfirmation === true ||
    workspace.status === "AcceptedByCustomer" ||
    workspace.status === "RejectedByCustomer" ||
    workspace.workspaceState === PRICE_WORKSPACE_STATE.NOT_SUBMITTED ||
    workspace.workspaceState === PRICE_WORKSPACE_STATE.AWAITING ||
    workspace.workspaceState === PRICE_WORKSPACE_STATE.ACCEPTED ||
    workspace.workspaceState === PRICE_WORKSPACE_STATE.REVIEW
  );
};

const resolveRequestPrimaryKey = (requestId) => {
  const numericId = Number(requestId);
  if (!requestId || Number.isNaN(numericId) || numericId <= 0) {
    return null;
  }
  return numericId;
};

const useCustomerPriceConfirmationWorkspace = (requestId) => {
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [error, setError] = useState(null);

  const requestPrimaryKey = useMemo(
    () => resolveRequestPrimaryKey(requestId),
    [requestId]
  );

  const reload = useCallback(async () => {
    if (!requestPrimaryKey) {
      setWorkspace(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await CustomerPriceOfferContractApi.getPriceConfirmationWorkspace(
        requestPrimaryKey
      );

      if (res.ok && res.data) {
        setWorkspace(normalizePriceConfirmationWorkspace(res.data));
        setError(null);
        return;
      }

      setWorkspace(null);
      const failure = {
        message: res.message || "خطا در دریافت workspace تأیید قیمت",
        status: res.statusCode ?? null,
      };
      setError(failure);

      if (process.env.NODE_ENV === "development") {
        console.error("[CustomerPriceConfirmationWorkspace] load failed", {
          requestId: requestPrimaryKey,
          status: failure.status,
          message: failure.message,
        });
      }
    } catch (err) {
      setWorkspace(null);
      const failure = {
        message: err?.message || "خطا در دریافت workspace تأیید قیمت",
        status: err?.response?.status ?? null,
      };
      setError(failure);

      if (process.env.NODE_ENV === "development") {
        console.error("[CustomerPriceConfirmationWorkspace] load error", {
          requestId: requestPrimaryKey,
          status: failure.status,
          message: failure.message,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [requestPrimaryKey]);

  useEffect(() => {
    reload();
  }, [reload]);

  const shouldShowPanel = !error && shouldShowCustomerPricingPanel(workspace);

  return {
    loading,
    error,
    workspace,
    shouldShowPanel,
    requestPrimaryKey,
    reload,
  };
};

export default useCustomerPriceConfirmationWorkspace;
